/**
 * POS 기기 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { posService } from '../../services/posService';
import { PosDevice, PosDeviceFilter } from '../../types/pos';

const PosDevicesList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<PosDeviceFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['posDevices', filter],
    queryFn: () => posService.getDevices(filter),
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">활성</Badge>;
      case 'inactive':
        return <Badge bg="secondary">비활성</Badge>;
      case 'maintenance':
        return <Badge bg="warning">점검중</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const columns: Column<PosDevice>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'device_name',
      label: '기기명',
      render: (device) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/pos/devices/${device.id}`)}
        >
          {device.device_name}
        </span>
      ),
    },
    {
      key: 'device_code',
      label: '기기 코드',
      render: (device) => (
        <span style={{ color: '#ccc' }}>{device.device_code}</span>
      ),
    },
    {
      key: 'location',
      label: '설치 위치',
      render: (device) => (
        <span style={{ color: '#ccc' }}>{device.location || '-'}</span>
      ),
    },
    {
      key: 'device_type',
      label: '기기 유형',
      render: (device) => (
        <span style={{ color: '#ccc' }}>{device.device_type || '-'}</span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (device) => getStatusBadge(device.status),
    },
    {
      key: 'last_sync_at',
      label: '최종 동기화',
      render: (device) => (
        <span style={{ color: '#ccc' }}>
          {device.last_sync_at ? new Date(device.last_sync_at).toLocaleString('ko-KR') : '-'}
        </span>
      ),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '기기명, 코드로 검색',
    },
    {
      name: 'device_type',
      label: '기기 유형',
      type: 'text',
      placeholder: '기기 유형으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
        { value: 'maintenance', label: '점검중' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (device: PosDevice) => {
    navigate(`/admin/pos/devices/${device.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>POS 기기 관리</h1>
          <div>
            <Button variant="info" onClick={() => navigate('/admin/pos/sales')} className="me-2">
              매출 관리
            </Button>
            <Button variant="primary" onClick={() => navigate('/admin/pos/devices/new')}>
              기기 등록
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.devices || []}
              loading={isLoading}
              emptyMessage="POS 기기가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PosDevicesList;

